import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { Button } from "~/components/Button";
import { Checkbox } from "~/components/Checkbox";
import { MaskedInput, TextInput } from "~/components/TextInput";
import {
	type LicenseExtensionFormData,
	licenseExtensionSchema,
} from "~/modules/sales/licenseExtensions/schemas";
import {
	createLicenseExtension,
	updateLicenseExtension,
} from "~/modules/sales/licenseExtensions/services";
import type { LicenseExtension } from "~/modules/sales/licenseExtensions/types";
import { insertToast } from "~/modules/toasts/services";

type Props = {
	extension: LicenseExtension | null;
	onSuccess: () => void;
	onDismiss: () => void;
};

export const LicenseExtensionForm: React.FC<Props> = ({
	extension,
	onSuccess,
	onDismiss,
}) => {
	const isEditing = extension !== null;

	const {
		register,
		control,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<LicenseExtensionFormData>({
		resolver: zodResolver(licenseExtensionSchema),
		defaultValues: { active: true },
	});

	useEffect(() => {
		if (extension) {
			reset({
				name: extension.name,
				licensesAmount: extension.licensesAmount,
				price: extension.price / 100,
				active: extension.active,
			});
		} else {
			reset({
				name: "",
				licensesAmount: undefined,
				price: undefined,
				active: true,
			});
		}
	}, [extension, reset]);

	const onSubmit = async (data: LicenseExtensionFormData) => {
		try {
			if (isEditing) {
				await updateLicenseExtension(extension.id, data);
			} else {
				await createLicenseExtension(data);
			}
			insertToast({
				duration: 3,
				title: "Sucesso!",
				message: isEditing ? "Pacote atualizado." : "Pacote criado.",
			});
			onSuccess();
		} catch {
			insertToast({
				duration: 4,
				title: "Erro!",
				message: "Não foi possível salvar o pacote.",
				type: "error",
			});
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
			<TextInput
				label="Nome"
				placeholder="Ex: Pacote 10 Licenças"
				{...register("name")}
				error={errors.name?.message}
			/>

			<Controller
				control={control}
				name="licensesAmount"
				render={({ field }) => (
					<MaskedInput
						mask="integer"
						label="Quantidade de licenças"
						placeholder="Ex: 10"
						inputMode="numeric"
						value={field.value}
						onChange={field.onChange}
						error={errors.licensesAmount?.message}
					/>
				)}
			/>

			<Controller
				control={control}
				name="price"
				render={({ field }) => (
					<MaskedInput
						mask="money"
						label="Preço do ciclo cheio"
						placeholder="R$ 0,00"
						inputMode="decimal"
						value={field.value}
						onChange={field.onChange}
						error={errors.price?.message}
					/>
				)}
			/>

			<Checkbox
				{...register("active")}
				label="Ativo (visível para personal trainers)"
			/>

			<div className="flex justify-end gap-3 mt-2">
				<button
					type="button"
					onClick={onDismiss}
					className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200"
				>
					Cancelar
				</button>
				<Button
					type="submit"
					loading={isSubmitting}
					loadingMessage="Salvando..."
				>
					{isEditing ? "Salvar alterações" : "Criar pacote"}
				</Button>
			</div>
		</form>
	);
};
